import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import blueSkyLogo from "../../src/assets/bluesky-logo.svg";
import userService from "../service/UserService";
import { auth } from "../firebase";
import { signInWithEmailAndPassword } from "firebase/auth";
import { useAuth } from "../components/AuthContext";
import mailer from "../service/api/mailer";
import partnerProfileService from "../service/partner/PartnerProfileService";
import CenterService from "../service/center/CenterService";
import Popup from "../components/Popup";
import Modal from "../components/Modal";
import { encrypt, decrypt } from "../service/encryption/Encryption";
import {
  collection,
  doc,
  addDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";

const AccessAccount = ({ isOpen, onClose, citizenData, orgType }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState(1);
  const [reason, setReason] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [accessCode, setAccessCode] = useState("");
  const [masterAccessCode, setMasterAccessCode] = useState("");
  const [masterCode, setMasterCode] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [user, setUser] = useState({});
  const [employee, setEmployee] = useState("");
  const [organization, setOrganization] = useState({});
  const [showPopup, setShowPopup] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [masterCodeError, setMasterCodeError] = useState(false);

  const { login, setRoleBasedRoute } = useAuth();
  const navigate = useNavigate();

  const employeeDetails = JSON.parse(sessionStorage.getItem("employeeDetails"));
  const selectedUserId = sessionStorage.getItem("userId");

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleNext = async () => {
    if (reason.trim() === "") {
      setErrorMessage("Reason for Access is required.");
      return;
    }

    setIsLoading(true);
    try {
      // Get organization name with fallback
      const orgName =
        orgType === "partner"
          ? organization?.name_of_org || "Organization"
          : organization?.name_of_hub || "Hub";

      const accessAccount = {
        access_code: encrypt(accessCode),
        master_access_code: encrypt(masterAccessCode),
        employee_id: employeeId,
        employee_user_id: employee.id,
        reason_for_access: reason,
        org_type: orgType,
        created_date: new Date(),
        expiration_date: new Date(new Date().setDate(new Date().getDate() + 1)),
      };

      // Save to user
      await userService.updateUser(citizenData.citizen_id, {
        access_acount: accessAccount,
      });

      // Create chat message
      const systemMessage = `This is an automated system message. ${orgName} has requested access to your account. An email containing the master access code has been sent to your registered email address.`;

      // Create chat ID by sorting user IDs
      const chatId = [selectedUserId, citizenData.citizen_id].sort().join("_");

      // Add message to chats collection
      await addDoc(collection(db, "chats", chatId, "messages"), {
        senderId: selectedUserId,
        message: systemMessage,
        timestamp: serverTimestamp(),
        seen: false,
        employeeId: employeeDetails.id,
      });

      // Ensure chat document exists
      await setDoc(
        doc(db, "chats", chatId),
        {
          users: [selectedUserId, citizenData.citizen_id],
          createdAt: serverTimestamp(),
        },
        { merge: true }
      );

      // Add to message-center collection
      await addDoc(collection(db, "message-center"), {
        active: true,
        global_notification: true,
        chat_recipients: [selectedUserId, citizenData.citizen_id],
        read_by: {
          [citizenData.citizen_id]: false,
        },
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
        type: "access_account_message",
        content: systemMessage,
        sender: {
          id: employeeDetails.id,
          name: `${employeeDetails?.first_name} ${employeeDetails?.last_name}`,
          email: employeeDetails.email,
          organization: orgName,
          role: employeeDetails.user_role,
        },
        chat_id: chatId,
        urgency: "notification",
        title: `New message from ${employeeDetails?.first_name} ${employeeDetails?.last_name}`,
        timestamp: serverTimestamp(),
      });

      // Email user
      const empName =
        employeeDetails?.first_name + " " + employeeDetails?.last_name;

      if (user?.email && employeeDetails?.mobile_number) {
        await mailer.sendAccessEmail(
          user.email,
          user.first_name || "",
          masterAccessCode,
          accessCode,
          orgName,
          empName,
          employeeId,
          employeeDetails?.is_admin
            ? organization.mobile_number
            : employeeDetails.mobile_number || "",
          reason
        );
      } else {
        if (!user.email) {
          setErrorMessage(
            "User has no email. Access will only be allowed if the citizen has authorized it."
          );
          return;
        }
      }

      setErrorMessage("");
      setStep(2);
    } catch (error) {
      console.error("Error in handleNext:", error);
      setErrorMessage("Failed to process request. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinue = async () => {
    if (masterCode !== masterAccessCode) {
      setMasterCodeError(true);
      setErrorMessage("Incorrect master code");
      return;
    }

    setMasterCodeError(false);
    setErrorMessage("");

    try {
      // Open new tab first
      const newWindow = window.open("", "_blank");

      // Set necessary data in the new tab's session storage
      newWindow.sessionStorage.setItem("userId", user.id);

      // Navigate the new tab to home
      newWindow.location.href = window.location.origin + "/home";

      // Close the modal in the original tab
      onClose();

      // Reset states
      setStep(1);
      setReason("");
      setMasterCode("");
      setErrorMessage("");
      setMasterCodeError(false);
    } catch (error) {
      console.error("Error switching user:", error);
      setErrorMessage("Failed to switch user. Please try again.");
    }
  };

  useEffect(() => {
    const fetchUserData = async () => {
      if (!citizenData) return;

      try {
        const tmpUser1 = await userService.getUserById(citizenData.citizen_id);
        if (!tmpUser1) throw new Error("User not found");
        setUser(tmpUser1);

        const tmpUser2 = await userService.getUserById(employeeDetails?.id);
        if (!tmpUser2) throw new Error("Employee not found");
        setEmployee(tmpUser2);

        // Only generate access codes if they haven't been set yet
        if (!accessCode) {
          setAccessCode(Math.floor(100000 + Math.random() * 900000).toString());
        }
        if (!masterAccessCode) {
          setMasterAccessCode(
            Math.floor(100000 + Math.random() * 900000).toString()
          );
        }

        if (tmpUser2.employee_id) {
          setEmployeeId(tmpUser2.employee_id);
        } else {
          const empId = Math.floor(
            100000 + Math.random() * 90000000
          ).toString();
          await userService.updateUser(tmpUser2.id, { employee_id: empId });
          setEmployeeId(empId);
        }

        const generalUserData = await userService.getUserById(selectedUserId);
        if (!generalUserData) throw new Error("Selected user not found");

        if (orgType === "partner") {
          if (!generalUserData.partner_id) {
            console.error("No partner_id found for user");
            return;
          }
          const org = await partnerProfileService.getPartnerById(
            generalUserData.partner_id
          );
          if (org) setOrganization(org);
        } else if (orgType === "center") {
          if (!generalUserData.center_id) {
            console.error("No center_id found for user");
            return;
          }
          const center = await CenterService.getCenterById(
            generalUserData.center_id
          );
          if (center) setOrganization(center);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        setErrorMessage("Failed to fetch user data. Please try again.");
      }
    };

    fetchUserData();
  }, [citizenData, orgType]);

  return (
    <>
      {/* Step 1 Modal */}
      <Modal
        isOpen={isOpen && step === 1}
        onClose={onClose}
        title="Access Account"
        customLeftButtonText="Cancel"
        customRightButtonText="Next"
        customOnRightButtonClick={handleNext}
        isLoading={isLoading}
      >
        <div className="flex flex-col gap-4">
          <p className="text-center text-xl font-bold text-[#3D435A]">
            Reason for Access
          </p>
          <input
            placeholder="Reason for Access"
            type="text"
            className="h-[40px] w-full border border-[#D5D7DB] p-[10px] font-[Inter] focus:outline-none"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          {errorMessage && (
            <p className="text-sm text-red-500">{errorMessage}</p>
          )}
        </div>
      </Modal>

      {/* Step 2 Modal */}
      <Modal
        isOpen={isOpen && step === 2}
        onClose={onClose}
        title="Access Account Verification"
        customLeftButtonText="Cancel"
        customRightButtonText="Continue"
        customOnRightButtonClick={handleContinue}
        isLoading={isLoading}
      >
        <div className="flex flex-col gap-4">
          <table className="w-full text-left">
            <tbody>
              <tr>
                <td className="text-gray-700 w-40 py-2 font-medium">
                  Reason for Access:
                </td>
                <td className="text-gray-600 py-2">{reason}</td>
              </tr>
              <tr>
                <td className="text-gray-700 w-40 py-2 font-medium">
                  Employee ID:
                </td>
                <td className="text-gray-600 py-2">{employeeId}</td>
              </tr>
              <tr>
                <td className="text-gray-700 w-40 py-2 font-medium">
                  Access Code:
                </td>
                <td className="text-gray-600 py-2">{accessCode}</td>
              </tr>
            </tbody>
          </table>
          <div className="relative">
            <input
              placeholder="Master Verification Code"
              type={showPassword ? "text" : "password"}
              className={`h-[40px] w-full border ${
                masterCodeError
                  ? "border-red-500 focus:border-red-500"
                  : "border-[#D5D7DB]"
              } p-[10px] pr-[40px] font-[Inter] focus:outline-none`}
              value={masterCode}
              onChange={(e) => {
                setMasterCode(e.target.value);
                setMasterCodeError(false);
                setErrorMessage("");
              }}
            />
            <button
              type="button"
              onClick={togglePasswordVisibility}
              className="absolute right-3 top-1/2 -translate-y-1/2 transform cursor-pointer text-blue-500"
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>
          {masterCodeError && (
            <p className="text-sm text-red-500">{errorMessage}</p>
          )}
        </div>
      </Modal>

      {/* Error Popup */}
      {showPopup && (
        <Popup
          title="Incorrect Code"
          message="The access code entered is invalid. Please verify the code provided in the email sent to the user's account."
          onClose={() => setShowPopup(false)}
        />
      )}
    </>
  );
};

export default AccessAccount;
